import { useReducer } from 'react';

import { toast } from 'react-toastify';

import { APIError } from '../../api/rest/shared';
import { updateUserProfile } from '../../api/rest/updateUserProfile';
import { countries } from '../../constants/countries';
import { GetAuthToken } from '../../contexts/AuthContext';
import { reportMykissClientError } from '../../helpers/errorHandling/client';
import { ErrorMessage, IValidator, validateForm } from '../../helpers/forms';
import { CurrentUser } from '../../helpers/hooks/useUserInfo';
import { IEditableUserAttributes } from '../../interfaces/rutilus/user';
import { SaveButton } from '../Clickables/Buttons/SaveButton';
import { ErrorBanner } from '../ErrorBanner/ErrorBanner';
import { FormInput } from '../Inputs/FormInput/FormInput';
import { IOptionType, Select } from '../Select/Select';

import { userProfileReducer } from './userProfileReducer';

interface IState {
  attributes: IEditableUserAttributes;
  errorMessages: ErrorMessage[];
  isSaving: boolean;
  didChange: boolean;
}

interface IProps {
  currentUser: CurrentUser;
  refetchUser: () => void;
  getAuthToken: GetAuthToken;
  labelClassName?: string;
}

const COUNTRIES = countries.map(({ iso2, name, unicodeFlag }) => ({
  value: iso2,
  label: `${unicodeFlag} ${name}`,
}));
const VALIDATORS: IValidator<IState>[] = [
  {
    errorMessage: 'First name cannot be empty.',
    rule: (state: IState) => state.attributes.first_name.length > 0,
  },
  {
    errorMessage: 'Last name cannot be empty.',
    rule: (state: IState) => state.attributes.last_name.length > 0,
  },
  {
    errorMessage: 'Email cannot be empty.',
    rule: ({ attributes: { email } }: IState) => typeof email === 'string' && email.length > 0,
  },
  {
    errorMessage: 'Nickname cannot be empty.',
    rule: (state: IState) => state.attributes.nickname.length > 0,
  },
];

export const UserProfileForm = (props: IProps) => {
  const { currentUser, refetchUser, getAuthToken, labelClassName } = props;

  if (!currentUser) {
    throw new Error('currentUser should be defined');
  }

  const initialState: IState = {
    attributes: {
      country_code: currentUser.countryCode,
      email: currentUser.privateFields.email || '',
      first_name: currentUser.firstName || '',
      last_name: currentUser.lastName || '',
      nickname: currentUser.nickname,
    },
    errorMessages: [],
    isSaving: false,
    didChange: false,
  };

  const [state, dispatch] = useReducer(userProfileReducer, initialState);

  const { attributes, errorMessages, isSaving, didChange } = state;

  const updateField = (field: string, value: string): void => {
    dispatch({ type: 'updateField', field, value });
  };

  const updateCountry = (value: IOptionType): void => {
    dispatch({ type: 'updateCountry', value });
  };

  const submit = (): void => {
    dispatch({ type: 'submit' });
    const formErrors = validateForm(VALIDATORS, state);

    if (formErrors.length !== 0) {
      dispatch({ type: 'submitError', errorMessages: formErrors });
      return;
    }

    updateUserProfile(attributes, getAuthToken)
      .then(() => {
        refetchUser();
        dispatch({ type: 'submitSuccess' });

        toast.success('Your Profile is updated successfully', { position: 'top-right' });
      })
      .catch((error: APIError) => {
        if (error instanceof Response && error.status === 422) {
          error
            .json()
            .then(body => {
              dispatch({ type: 'submitError', errorMessages: body.errors });
            })
            .catch(reportMykissClientError);
        } else {
          dispatch({
            type: 'submitError',
            errorMessages: [
              {
                errorMessage: error instanceof Response ? error.statusText : 'Something went wrong',
              },
            ],
          });
          reportMykissClientError(error);
        }
      });
  };

  return (
    <div data-testid="UserProfileForm">
      <ErrorBanner errors={errorMessages} />
      <fieldset>
        <FormInput
          id="first-name"
          label="First name"
          onChange={value => {
            updateField('first_name', value);
          }}
          value={attributes.first_name}
          className="mb-4"
          fullWidth
          labelClassName={labelClassName}
        />
        <FormInput
          id="last-name"
          label="Last name"
          onChange={value => {
            updateField('last_name', value);
          }}
          value={attributes.last_name}
          className="mb-4"
          fullWidth
          labelClassName={labelClassName}
        />
        <FormInput
          id="email"
          label="Email"
          onChange={value => {
            updateField('email', value);
          }}
          value={attributes.email || ''}
          inputProps={{ type: 'email' }}
          className="mb-4"
          fullWidth
          labelClassName={labelClassName}
        />
        <FormInput
          id="nickname"
          label="Username"
          onChange={value => {
            updateField('nickname', value);
          }}
          value={attributes.nickname}
          className="mb-4"
          fullWidth
          labelClassName={labelClassName}
        />
        <div className="mt-3 mb-4">
          <Select
            id="country"
            label="Country"
            onChange={value => {
              updateCountry(value as IOptionType);
            }}
            options={COUNTRIES}
            value={COUNTRIES.find(c => c.value === attributes.country_code)}
            labelClassName={labelClassName}
          />
        </div>
      </fieldset>
      <SaveButton className="w-full" isSaving={isSaving} onClick={submit} disabled={!didChange}>
        Update
      </SaveButton>
    </div>
  );
};
